// pushNotifications.ts
// FCM token registration and management.
// Called from NotificationPreferences after the user grants browser permission.

import { getMessaging, getToken, deleteToken } from 'firebase/messaging';
import firebaseApp from './firebaseConfig';
import { supabase } from './supabaseClient';

const VAPID_KEY = process.env.REACT_APP_FCM_VAPID_KEY ?? '';

export interface FCMRegistrationResult {
  token: string | null;
  /** Human-readable reason when token is null (surfaced in the UI so failures are never silent) */
  error: string | null;
}

/** Fire-and-forget: record the attempt server-side so device registration
 *  problems are diagnosable from the admin side (silent login-path included). */
async function logRegistrationAttempt(userId: string, success: boolean, error: string | null): Promise<void> {
  try {
    await supabase.from('push_registration_log').insert({
      user_id: userId,
      success,
      error,
      origin: window.location.origin,
      user_agent: navigator.userAgent.slice(0, 200),
    });
  } catch { /* logging must never break registration */ }
}

/**
 * Register this browser/device for FCM push notifications.
 * - Requests an FCM token using the VAPID key
 * - Upserts the token into push_subscriptions table
 * Returns { token, error } — error explains why registration failed.
 * Every attempt (success or failure) is also logged to push_registration_log.
 */
export async function registerFCMToken(userId: string): Promise<FCMRegistrationResult> {
  const result = await registerFCMTokenInternal(userId);
  const permState = ('Notification' in window) ? Notification.permission : 'unsupported';
  logRegistrationAttempt(
    userId,
    result.token !== null,
    result.error ? `${result.error} (permission=${permState})` : null,
  );
  return result;
}

async function registerFCMTokenInternal(userId: string): Promise<FCMRegistrationResult> {
  if (!VAPID_KEY) {
    console.warn('[FCM] REACT_APP_FCM_VAPID_KEY not set — skipping token registration');
    return { token: null, error: 'Push configuration missing (VAPID key not set in this build)' };
  }

  if (!('serviceWorker' in navigator)) {
    console.warn('[FCM] Service workers not supported in this browser');
    return { token: null, error: 'This browser does not support push notifications' };
  }

  try {
    const messaging = getMessaging(firebaseApp);

    // service-worker.js now has Firebase messaging imported, so we can use
    // it directly. navigator.serviceWorker.ready waits for ANY active SW —
    // in production it resolves with service-worker.js (already active).
    // In dev (CRA, no SW installed) it times out and we fall back to
    // firebase-messaging-sw.js which also has Firebase.
    let swReg: ServiceWorkerRegistration;
    try {
      swReg = await Promise.race<ServiceWorkerRegistration>([
        navigator.serviceWorker.ready,
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('sw-ready-timeout')), 8000)
        ),
      ]);
    } catch {
      // Dev / browser without active SW — register the standalone firebase bridge
      swReg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    }

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    });

    if (!token) {
      console.warn('[FCM] getToken returned empty — permission may have been denied');
      return { token: null, error: 'Browser did not issue a push token (permission may have been denied)' };
    }

    // Upsert token into DB — handles re-registration on token refresh
    const { error } = await supabase
      .from('push_subscriptions')
      .upsert(
        {
          user_id:        userId,
          fcm_token:      token,
          device_type:    getDeviceType(),
          user_agent:     navigator.userAgent.slice(0, 200),
          last_active_at: new Date().toISOString(),
        },
        { onConflict: 'user_id,fcm_token' }
      );

    if (error) {
      console.error('[FCM] Failed to save token to DB:', error.message);
      return { token: null, error: `Could not save device registration: ${error.message}` };
    }

    console.log('[FCM] ✅ Token registered successfully');
    return { token, error: null };
  } catch (err: any) {
    // Common case: user dismissed the permission dialog (not an error)
    if (err?.code === 'messaging/permission-blocked') {
      console.warn('[FCM] Push notifications blocked by user');
      return { token: null, error: 'Notifications are blocked for this site in your browser settings' };
    }
    console.error('[FCM] Token registration error:', err?.message ?? err);
    return { token: null, error: err?.message ?? 'Unknown registration error' };
  }
}

/**
 * Unregister FCM token on sign-out — removes it from DB so no
 * notifications are sent to this device after the user logs out.
 */
export async function unregisterFCMToken(userId: string): Promise<void> {
  try {
    const messaging = getMessaging(firebaseApp);
    const swReg = await navigator.serviceWorker.ready;

    const token = await getToken(messaging, {
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: swReg,
    }).catch(() => null);

    if (token) {
      // Delete from FCM
      await deleteToken(messaging).catch(() => {});
      // Delete from DB
      await supabase
        .from('push_subscriptions')
        .delete()
        .eq('user_id', userId)
        .eq('fcm_token', token);
    }
  } catch (err) {
    // Non-critical on sign-out — don't block
    console.warn('[FCM] Could not unregister token on sign-out:', err);
  }
}

/** Detect device type from user agent for push_subscriptions.device_type */
function getDeviceType(): 'web' | 'android' | 'ios' {
  const ua = navigator.userAgent.toLowerCase();
  if (/android/.test(ua)) return 'android';
  if (/iphone|ipad|ipod/.test(ua)) return 'ios';
  return 'web';
}
